/**
 * Makes a folder tile, card or row a drop target: it arms after a 300ms hover
 * (`dragState`), then
 *
 * - OS files dropped on it upload INTO it (`useDropUpload`); the drop is
 *   marked as taken so the canvas overlay does not upload it again. A file
 *   drop on a folder that never armed is left to the canvas (open folder).
 * - items dropped on it (the shared tree/canvas payload) move into it with
 *   one bulk move (`useBulkMove`: optimistic, with undo). A read-only folder,
 *   the folder itself, or a folder inside a dragged folder is refused with a
 *   toast before any request. A canvas drag (whose items are known while it
 *   hovers) shows not-allowed over such a folder and never arms it.
 *
 * Anything else (text, links) is ignored, so the browser shows no drop.
 * One hook per view; the returned builder is hook-free, for grid rows.
 */

import { useCallback, type DragEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { WorkspaceItem } from '@bower/shared';

import { useWorkspaceId } from '../../../contexts/WorkspaceContext';
import { moveFailureText, useBulkMove } from '../../../hooks/workspace/useBulkMove';
import { useAppToast } from '../../../hooks/ui/useAppToast';
import { queryKeys } from '../../../lib/queryClient';
import { isFileDrag, isItemDrag, readItemDragPayload } from '../../navigation/HierarchyTree.dnd';
import {
  armedDropTargetId,
  draggingItems,
  hoverDropTarget,
  leaveDropTarget,
  markDropHandled,
  resetDropTarget,
} from './dragState';
import { moveRefusal } from './dropRules';
import { useDropUpload } from './useDropUpload';

type Path = ReadonlyArray<{ readonly id: string }>;

export interface FolderDropProps {
  'data-drop-folder-id': string;
  onDragEnter: (event: DragEvent<HTMLElement>) => void;
  onDragOver: (event: DragEvent<HTMLElement>) => void;
  onDragLeave: (event: DragEvent<HTMLElement>) => void;
  onDrop: (event: DragEvent<HTMLElement>) => void;
}

export function useFolderDropTarget(openFolderId: string | null) {
  const workspaceId = useWorkspaceId() ?? '';
  const queryClient = useQueryClient();
  const { mutateAsync: bulkMove } = useBulkMove();
  const uploadDrop = useDropUpload();
  const toast = useAppToast();

  /** Every folder above `target` the client knows: the open path, the target's own path, its location. */
  const ancestorsOf = useCallback(
    (target: WorkspaceItem): Set<string> => {
      const pathOf = (id: string | null): Path =>
        id ? (queryClient.getQueryData<Path>(queryKeys.folderPath(workspaceId, id)) ?? []) : [];
      return new Set([
        ...pathOf(openFolderId).map((f) => f.id),
        ...pathOf(target.parentId).map((f) => f.id),
        ...(target.location ?? []).map((f) => f.id),
        ...(target.parentId ? [target.parentId] : []),
      ]);
    },
    [queryClient, workspaceId, openFolderId],
  );

  return useCallback(
    (target: WorkspaceItem): FolderDropProps | undefined => {
      if (target.kind !== 'folder') return undefined;
      const hover = (event: DragEvent<HTMLElement>) => {
        const { dataTransfer } = event;
        if (!isFileDrag(dataTransfer) && !isItemDrag(dataTransfer)) return;
        event.preventDefault();
        const upload = isFileDrag(dataTransfer);
        // A canvas drag the drop would refuse (a folder into its own
        // descendant, anything into a read-only folder): not-allowed while
        // hovering, never armed, so it never looks droppable. The same rule
        // as the drop's; a tree drag is only known at the drop.
        const dragged = upload ? [] : draggingItems();
        if (dragged.length > 0 && moveRefusal(dragged, target, ancestorsOf(target))) {
          event.stopPropagation();
          dataTransfer.dropEffect = 'none';
          leaveDropTarget(target.id);
          return;
        }
        dataTransfer.dropEffect = upload ? 'copy' : 'move';
        hoverDropTarget(target.id, upload ? 'upload' : 'move');
      };
      return {
        'data-drop-folder-id': target.id,
        onDragEnter: hover,
        onDragOver: hover,
        onDragLeave: (event) => {
          if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
          leaveDropTarget(target.id);
        },
        onDrop: (event) => {
          const { dataTransfer } = event;
          const armed = armedDropTargetId() === target.id;
          resetDropTarget();
          if (!armed) return;
          if (isFileDrag(dataTransfer)) {
            event.preventDefault();
            markDropHandled(event.nativeEvent);
            uploadDrop(Array.from(dataTransfer.files), target);
            return;
          }
          const dragged = readItemDragPayload(dataTransfer);
          if (dragged.length === 0) return;
          event.preventDefault();
          event.stopPropagation();
          const refusal = moveRefusal(dragged, target, ancestorsOf(target));
          if (refusal) {
            toast({ title: 'Nothing moved', description: refusal, status: 'warning' });
            return;
          }
          // The promise, not a per-call onError: a move that takes the rows
          // off screen can unmount this observer before it settles.
          bulkMove({ items: dragged, targetFolderId: target.id, targetName: target.name }).catch(
            (error: unknown) =>
              toast({
                title: 'Could not move',
                description: moveFailureText(error, dragged),
                status: 'error',
              }),
          );
        },
      };
    },
    [ancestorsOf, bulkMove, toast, uploadDrop],
  );
}
